"use client";

import { Team } from "@/types";
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@heroui/react";
import { cn } from "@heroui/theme";
import { useTranslations } from "next-intl";
import { useState } from "react";

interface ChallengeButtonProps {
  targetTeam: Team;
  onChallenge: (teamId: string) => void;
  cardType?: "joker" | "braveheart";
  isSubmitting?: boolean;
  className?: string;
  disabled?: boolean;
  vacationStartsInDays?: number;
  /** Target team has forfeited — challenge resolves as instant WO win. */
  isWalkover?: boolean;
}

export function ChallengeButton({
  targetTeam,
  onChallenge,
  cardType,
  isSubmitting = false,
  className,
  disabled = false,
  vacationStartsInDays,
  isWalkover = false,
}: ChallengeButtonProps) {
  const t = useTranslations();
  const [showConfirmation, setShowConfirmation] = useState(false);

  const handleConfirm = () => {
    onChallenge(targetTeam.id);
    setShowConfirmation(false);
  };

  const handleCancel = () => {
    setShowConfirmation(false);
  };

  const handleInitialClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowConfirmation(true);
  };

  const handleModalClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  // Determine if button should be disabled due to vacation
  const isVacationDisabled = disabled && vacationStartsInDays !== undefined;
  const isButtonDisabled = isSubmitting || disabled;

  const bodyGradientId = isVacationDisabled
    ? "fc-ch-body-disabled"
    : cardType === "joker"
      ? "fc-ch-body-joker"
      : cardType === "braveheart"
        ? "fc-ch-body-braveheart"
        : "fc-ch-body";

  return (
    <>
      <button
        type="button"
        onClick={handleInitialClick}
        disabled={isButtonDisabled}
        className={cn(
          "fc-challenge",
          cardType === "joker" && "fc-challenge-joker",
          cardType === "braveheart" && "fc-challenge-braveheart",
          isVacationDisabled && "fc-challenge-disabled",
          className,
        )}
      >
        <svg
          className="fc-challenge-shape"
          viewBox="0 0 280 50"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <path
            d="M 24 0 L 256 0 L 280 25 L 256 50 L 24 50 L 0 25 Z"
            fill={`url(#${bodyGradientId})`}
          />
          <path
            d="M 40 3 L 240 3 L 246 13 L 34 13 Z"
            fill="url(#fc-ch-hl)"
          />
          <path
            d="M 22 38 L 258 38 L 248 47 L 32 47 Z"
            fill="rgba(0,0,0,0.3)"
          />
          <path
            d="M 0 25 L 24 0 L 38 0 L 30 7 L 34 13 L 28 20 L 38 25 L 28 30 L 34 37 L 30 43 L 38 50 L 24 50 Z"
            fill="url(#fc-ch-dark)"
          />
          <path
            d="M 280 25 L 256 0 L 242 0 L 250 7 L 246 13 L 252 20 L 242 25 L 252 30 L 246 37 L 250 43 L 242 50 L 256 50 Z"
            fill="url(#fc-ch-dark)"
          />
          <path
            d="M 0 25 L 24 0 L 24 1 L 1 25 L 24 49 L 24 50 Z"
            fill="rgba(124,200,188,0.25)"
          />
          <path
            d="M 280 25 L 256 0 L 256 1 L 279 25 L 256 49 L 256 50 Z"
            fill="rgba(124,200,188,0.25)"
          />
          <circle cx="38" cy="13" r="1.8" fill="url(#fc-ch-rivet)" />
          <circle cx="38" cy="37" r="1.8" fill="url(#fc-ch-rivet)" />
          <circle cx="242" cy="13" r="1.8" fill="url(#fc-ch-rivet)" />
          <circle cx="242" cy="37" r="1.8" fill="url(#fc-ch-rivet)" />
          <circle cx="5" cy="25" r="2.4" fill="url(#fc-ch-gem)" />
          <circle cx="275" cy="25" r="2.4" fill="url(#fc-ch-gem)" />
        </svg>
        <span className="fc-challenge-content">
          {!isVacationDisabled && (
            <span className="fc-challenge-label">{t("tower.challenge")}</span>
          )}

          {isVacationDisabled && vacationStartsInDays !== undefined ? (
            <span className="fc-challenge-sub">
              {t("tower.vacationStartsInDays", { days: vacationStartsInDays })}
            </span>
          ) : (
            cardType && (
              <span className="fc-challenge-sub">
                {cardType === "joker" && t("tower.withJokerCard")}
                {cardType === "braveheart" && t("tower.withBraveheartCard")}
              </span>
            )
          )}
        </span>
      </button>

      { }
      <div onClick={handleModalClick}>
        <Modal
          isOpen={showConfirmation}
          onClose={handleCancel}
          isDismissable={false}
          isKeyboardDismissDisabled={true}
          hideCloseButton
          placement="center"
          backdrop="opaque"
          size="sm"
          className="medieval-modal"
          classNames={{
            backdrop: "bg-black/80",
            base: "bg-[var(--bg-card)] border border-[var(--border-gold)] shadow-2xl",
            header: "border-b border-[var(--border-subtle)]",
            body: "py-4",
            footer: "border-t border-[var(--border-subtle)]",
          }}
        >
          <ModalContent>
            <ModalHeader className="flex flex-col gap-1 text-primary font-cinzel">
              <h3 className="text-lg">
                ⚔️ {t("tower.confirmChallenge.title")}
              </h3>
            </ModalHeader>
            <ModalBody>
              <p className="text-secondary text-sm">
                {t("tower.confirmChallenge.message", {
                  teamName: targetTeam.name ?? "",
                })}
              </p>
              {cardType && (
                <p
                  className={cn(
                    "text-sm mt-2 font-medium",
                    cardType === "joker" && "text-purple-400",
                    cardType === "braveheart" && "text-rose-400",
                  )}
                >
                  {cardType === "joker" &&
                    `🃏 ${t("tower.confirmChallenge.requiresJokerCard")}`}
                  {cardType === "braveheart" &&
                    `💪 ${t("tower.confirmChallenge.requiresBraveheartCard")}`}
                </p>
              )}
              {isWalkover && (
                <p className="text-sm mt-2 font-medium text-rose-400">
                  🏳️ {t("tower.confirmChallenge.walkoverNotice")}
                </p>
              )}
            </ModalBody>
            <ModalFooter className="gap-2">
              <Button
                variant="flat"
                onPress={handleCancel}
                isDisabled={isSubmitting}
                className="flex-1 bg-transparent border border-[var(--border-subtle)] text-secondary hover:border-[var(--border-gold)] hover:text-[var(--gold)]"
              >
                {t("tower.common.no")}
              </Button>
              <Button
                onPress={handleConfirm}
                isDisabled={isSubmitting}
                isLoading={isSubmitting}
                className={cn(
                  "flex-1 font-semibold text-white",
                  cardType === "joker" &&
                    "bg-gradient-to-r from-purple-600 to-purple-500 hover:shadow-lg hover:shadow-purple-500/50",
                  cardType === "braveheart" &&
                    "bg-gradient-to-r from-rose-600 to-rose-500 hover:shadow-lg hover:shadow-rose-500/50",
                  !cardType &&
                    "bg-gradient-to-r from-[var(--gold-dark)] to-[var(--gold)] text-black hover:shadow-lg hover:shadow-[var(--gold)]/50",
                )}
              >
                {t("tower.common.yes")}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </div>
    </>
  );
}
